import { EWorldMapSetType } from '__generated__/globalTypes';
import { WorldMapMarkerStory_worldMapView_markers } from 'graphql/query/__generated__/WorldMapMarkerStory';
import { intersection } from 'lodash';
import { useCallback, useContext } from 'react';
import { CtxView } from 'session/ctxView';
import { MAP_TO_MARKERS_CONFIG } from 'static/config/custom/markersConfig';

export const useMarkerVisibilityView = () => {
    const ctxView = useContext(CtxView);

    const worldMapId = ctxView.map?.worldMapId;

    const markers = ctxView.allMarkers ?? [];

    const staticSelectableMarkers =
        MAP_TO_MARKERS_CONFIG.find((e) => e.worldMapId === worldMapId)?.markers ?? [];

    const storiesMap = new Map(ctxView.allDetails?.map((e) => [e.storyId, e]));
    const mapCategoryIds = ctxView.map?.worldMapSets
        ?.filter((e) => e.type === EWorldMapSetType.CATEGORY)
        .map((e) => e.worldMapSetId);

    const visibleSets = new Set(ctxView.visibleMapSetIds);

    const visibleMarkersStoriesByCategoriesSet = new Set(
        markers
            .filter((marker) => {
                // If it's not in any CATEGORY set, then it's always visible
                const sets =
                    marker.worldMapSets.filter((e) => e.type === EWorldMapSetType.CATEGORY) ?? [];
                if (sets.length === 0) {
                    return true;
                }

                return sets.some((set) => visibleSets.has(set.worldMapSetId));
            })
            .map((e) => e.worldMapMarkerId)
    );

    const visibleMarkersStoriesByFloorSet = new Set(
        markers
            .filter((marker) => {
                // If it's not in any CATEGORY set, then it's always visible
                const sets =
                    marker.worldMapSets.filter((e) => e.type === EWorldMapSetType.FLOOR) ?? [];
                if (sets.length === 0) {
                    return true;
                }
                return sets.some((set) => visibleSets.has(set.worldMapSetId));
            })
            .map((e) => e.worldMapMarkerId)
    );

    // if no category is selected, return true for all markers
    // if category is selected, go through all markers
    // for markers that do not have any storyId linked, return true
    // for markers that have some storyId linked, check all the storyIds
    // if at least one is in the selected category, return true and if no, return false

    const visibleByStoryCategorySet = new Set(
        intersection(mapCategoryIds, ctxView.visibleMapSetIds).length === mapCategoryIds?.length
            ? // All categories are visible
              markers.map((e) => e.worldMapMarkerId)
            : // Only some categories are visible
              markers
                  .filter((marker) => {
                      if (marker.stories?.length === 0) {
                          return true;
                      }

                      const stories = marker.stories
                          ?.map((e) => storiesMap.get(e.storyId))
                          .filter(Boolean);
                      const categoryIds = stories.flatMap((e) =>
                          e.categories?.map((c) => c.worldMapSetId)
                      );

                      if (intersection(categoryIds, ctxView.visibleMapSetIds).length > 0) {
                          return true;
                      }
                      return false;
                  })
                  .map((e) => e.worldMapMarkerId)
    );

    const isMarkerVisibleInViewMap = (marker: WorldMapMarkerStory_worldMapView_markers) =>
        marker.visible &&
        visibleMarkersStoriesByFloorSet.has(marker.worldMapMarkerId) &&
        visibleMarkersStoriesByCategoriesSet.has(marker.worldMapMarkerId) &&
        visibleByStoryCategorySet.has(marker.worldMapMarkerId);

    const isMarkerStoryVisibleInViewList = useCallback(
        (marker: WorldMapMarkerStory_worldMapView_markers) => {
            const hasStories = marker.stories.length;
            if (!hasStories) {
                return false;
            }

            const allExcluded = marker.stories.every((story) =>
                ctxView.customPageConfig.storyIdsExcludedInList?.includes(story.storyId)
            );

            if (allExcluded) {
                return false;
            }

            return visibleMarkersStoriesByCategoriesSet.has(marker.worldMapMarkerId);
        },
        [visibleMarkersStoriesByCategoriesSet]
    );

    const isMarkerSelectableInView = (marker: WorldMapMarkerStory_worldMapView_markers) =>
        marker.stories.length ||
        staticSelectableMarkers.some((e) => e.markerId === marker.worldMapMarkerId);

    return {
        isMarkerVisibleInViewMap,
        isMarkerStoryVisibleInViewList,
        isMarkerSelectableInView
    };
};
